import { z } from "zod";
import { isoDateTimeSchema, uuidSchema } from "./common.js";

/** The entity kinds a file can carry, one per import template (docs/SYNTHETIC_DATA.md section 4). */
export const importEntityKindSchema = z.enum([
  "supply_points",
  "premises",
  "meters",
  "reads",
  "field_jobs",
  "evidence",
  "address_base",
  "connections",
]);
export type ImportEntityKind = z.infer<typeof importEntityKindSchema>;

export const importStatusSchema = z.enum([
  "validating",
  "ready_to_commit",
  "committing",
  "committed",
  "failed",
]);
export type ImportStatus = z.infer<typeof importStatusSchema>;

export const importCountsSchema = z.object({
  read: z.number().int().nonnegative(),
  staged: z.number().int().nonnegative(),
  quarantined: z.number().int().nonnegative(),
  duplicates: z.number().int().nonnegative(),
  referentialGaps: z.number().int().nonnegative(),
});
export type ImportCounts = z.infer<typeof importCountsSchema>;

/** GET /imports item (D-31: every import belongs to a connection and a sync). */
export const importSchema = z.object({
  id: uuidSchema,
  entityKind: importEntityKindSchema,
  connectionId: z.string(),
  syncId: uuidSchema.nullable(),
  mappingId: uuidSchema.nullable(),
  fileName: z.string(),
  contentHash: z.string().regex(/^[a-f0-9]{64}$/),
  status: importStatusSchema,
  detail: z.string().nullable(),
  counts: importCountsSchema,
  quarantineByReason: z.record(z.number().int().nonnegative()),
  createdAt: isoDateTimeSchema,
  committedAt: isoDateTimeSchema.nullable(),
});
export type Import = z.infer<typeof importSchema>;

export const quarantinedRowSchema = z.object({
  line: z.number().int().positive(),
  reason: z.string(),
  detail: z.string(),
  raw: z.record(z.unknown()),
});
export type QuarantinedRow = z.infer<typeof quarantinedRowSchema>;

/** A saved column mapping: template column name to the source file's column name (IMP-02). */
export const importMappingSchema = z.object({
  id: uuidSchema.optional(),
  name: z.string().min(1),
  entityKind: importEntityKindSchema,
  columns: z.record(z.string().min(1)),
  dateFormat: z.string().optional(),
});
export type ImportMapping = z.infer<typeof importMappingSchema>;
